/**
 * autoMode — le mode autonome d'un cours de groupe, en Concentré, quand la liste est vide.
 *
 * Les règles de l'ancien (`common/hooks/useAutoModeMaybeOpen.js`,
 * `common/liveMode/autoMode/AutoEvalOverlay.js`, `AutoModeChoiceModal.js`,
 * `common/hooks/useAutoModeReplaySame.js`), à l'identique :
 *  - il s'ouvre à la fin d'une activité de l'élève, en live, dans un cours de groupe (tout type sauf
 *    `individual` et `trial`), en Concentré, la liste « à faire » vide ;
 *  - Sam demande comment ça s'est passé : « Tout compris ! », « Pas tout compris », « Bloqué » ;
 *  - selon la réponse, 3 activités générées (2 si l'élève est bloqué), d'intentions distinctes, sur
 *    le support de l'activité finie, et « Refaire la même activité » ;
 *  - le choix crée l'activité (`POST api/v2/activities/todo`, le quiz de prérequis aux niveaux 1 et
 *    2 si la salle l'a), prévient le tuteur seul (`autoModeActivityStarted`, la réponse et
 *    l'activité ; `replay` pour la même) et l'ouvre.
 *
 * Ici le réducteur pur, testé en rejouant des suites d'événements, et la liste des appels.
 */
export type AutoModeEvaluation = "got_it" | "mixed" | "lost"

export type AutoModeIntent =
    "more_difficult" | "other_concepts" | "same_diversified" | "same_difficulty" | "concept_review" | "method_review"

/** Le support d'une activité, tel que le serveur le sert : ce que les propositions affichent. */
export type AutoModeMaterial = {
    id: number | string
    level?: number | string | null
    estimated_duration?: number | null
    school_subject?: { id?: number | string; name?: string | null } | null
} & Record<string, unknown>

export type AutoModeProposal = { intent: AutoModeIntent; material: AutoModeMaterial }

/** L'activité finie qui ouvre le mode : son support et son élève. */
export type AutoModeSource = {
    id?: number | string | null
    student_id?: number | string | null
    material?: { id?: number | string | null } | null
} & Record<string, unknown>

export type AutoModeState =
    | { phase: "closed" }
    | { phase: "evaluating"; source: AutoModeSource }
    | {
          phase: "choosing"
          source: AutoModeSource
          evaluation: AutoModeEvaluation
          loading: boolean
          proposals: AutoModeProposal[]
          error: "unavailable" | "generation" | null
          /** Le choix part : la proposition choisie, ou `replay`. */
          starting: string | null
          startError: boolean
      }

/**
 * Les moments sonores de l'ancien mode autonome : Sam demande comment ça s'est passé (`AutoEvalOverlay`),
 * les propositions sont arrivées (`AutoModeChoiceModal`), l'activité choisie ou refaite est lancée
 * (`AutoModeChoiceModal`, `useAutoModeReplaySame`).
 */
export type AutoModeCue = "auto_mode_opened" | "auto_mode_proposals" | "auto_mode_started"

export type AutoModeEvent =
    | { type: "OPEN"; source: AutoModeSource }
    | { type: "EVALUATED"; evaluation: AutoModeEvaluation; requests: number }
    | { type: "PROPOSAL"; proposal: AutoModeProposal }
    | { type: "GENERATED"; count: number }
    | { type: "CHOSEN"; key: string }
    | { type: "START_FAILED" }
    | { type: "CLOSE" }

export const initialAutoModeState: AutoModeState = { phase: "closed" }

export function autoModeReducer(state: AutoModeState, event: AutoModeEvent): AutoModeState {
    switch (event.type) {
        case "OPEN":
            return state.phase === "closed" ? { phase: "evaluating", source: event.source } : state
        case "EVALUATED":
            if (state.phase !== "evaluating") return state
            return {
                phase: "choosing",
                source: state.source,
                evaluation: event.evaluation,
                loading: event.requests > 0,
                proposals: [],
                error: event.requests > 0 ? null : "unavailable",
                starting: null,
                startError: false,
            }
        case "PROPOSAL":
            if (state.phase !== "choosing") return state
            return { ...state, proposals: [...state.proposals, event.proposal] }
        case "GENERATED":
            if (state.phase !== "choosing") return state
            return { ...state, loading: false, error: event.count === 0 ? "generation" : state.error }
        case "CHOSEN":
            if (state.phase !== "choosing" || state.starting != null) return state
            return { ...state, starting: event.key, startError: false }
        case "START_FAILED":
            if (state.phase !== "choosing") return state
            return { ...state, starting: null, startError: true }
        case "CLOSE":
            return initialAutoModeState
        default:
            return state
    }
}

/** Les types de séance sans mode autonome : l'individuel et l'essai. Un type inconnu ne l'ouvre pas. */
const EXCLUDED_ROOM_TYPES = ["individual", "trial"]

/** Le mode s'ouvre-t-il à la fin de cette activité ? */
export function autoModeOpens(args: {
    roomType: string | null | undefined
    dialogue: boolean
    todoCount: number
    source: AutoModeSource | null | undefined
    studentId: number | string | null | undefined
}): boolean {
    const { roomType, dialogue, todoCount, source, studentId } = args
    if (source?.id == null) return false
    if (roomType == null || roomType === "" || EXCLUDED_ROOM_TYPES.includes(roomType)) return false
    if (studentId == null || String(source.student_id) !== String(studentId)) return false
    // En Dialogue, l'élève est avec le tuteur : le mode ne lui vole pas son attention.
    if (dialogue) return false
    return todoCount === 0
}

/** Un appel de génération : le service, ses paramètres de route et son corps. */
export type AutoModeRequest = {
    intent: AutoModeIntent
    service:
        | "generateSimilarExercisesMoreDifficult"
        | "generateSimilarExercisesOtherConcepts"
        | "generateSimilarExercisesSameDifficultyDiversified"
        | "generateSimilarExercisesSameDifficulty"
        | "generatePropositionConceptReview"
        | "generatePropositionMethodReview"
    routeParams: Record<string, unknown>
    body: Record<string, unknown>
}

/**
 * Les appels de chaque réponse, trois intentions distinctes (deux pour « Bloqué ») : celles de
 * l'ancien `buildRequests`. Sans support, rien ne se génère.
 */
export function autoModeRequests(
    evaluation: AutoModeEvaluation,
    source: AutoModeSource,
    batchId: string
): AutoModeRequest[] {
    const materialId = source.material?.id
    if (materialId == null) return []
    const body = { material_id: materialId, request_batch_id: batchId }
    const similar = (intent: AutoModeIntent, service: AutoModeRequest["service"]): AutoModeRequest => ({
        intent,
        service,
        routeParams: {},
        body,
    })
    if (evaluation === "got_it") {
        return [
            similar("more_difficult", "generateSimilarExercisesMoreDifficult"),
            similar("other_concepts", "generateSimilarExercisesOtherConcepts"),
            similar("same_diversified", "generateSimilarExercisesSameDifficultyDiversified"),
        ]
    }
    if (evaluation === "mixed") {
        return [
            similar("same_difficulty", "generateSimilarExercisesSameDifficulty"),
            similar("same_diversified", "generateSimilarExercisesSameDifficultyDiversified"),
            similar("other_concepts", "generateSimilarExercisesOtherConcepts"),
        ]
    }
    const lost = (order: number) => ({ num_clusters: 1, order, request_batch_id: batchId })
    return [
        {
            intent: "concept_review",
            service: "generatePropositionConceptReview",
            routeParams: { materialId },
            body: lost(1),
        },
        {
            intent: "method_review",
            service: "generatePropositionMethodReview",
            routeParams: { materialId },
            body: lost(2),
        },
    ]
}

/** Le quiz de prérequis d'une activité choisie : niveaux 1 et 2, si la salle a la fonction. */
export function autoModeWantsQuiz(material: AutoModeMaterial | null | undefined, roomHasQuiz: boolean): boolean {
    const level = Number(material?.level)
    return roomHasQuiz && (level === 1 || level === 2)
}
